// models
import { AxiosInstance } from 'axios';

// models
import type {
  CompanyInfo,
  ICompanyDetail,
  ReqCompanyDetail,
  ReqCompanyList,
  ReqUpdateCompany,
  ResCompanyDetail,
  ResCompanyList,
  ResCompanyNames
} from '../models/company.model';

// modules
import type { BaseApiResponse, BaseSearchRequest } from 'modules';

// services
import AxiosInstanceCreator from 'services/api';

const companyInstance = new AxiosInstanceCreator().create();

export const companyApi = {
  /**
   * 업체 조회 API
   * 업체 페이지를 조회합니다.
   *
   * keyword 검색 범위 : 회사명, 대표자 이름, 프로젝트 매니저 이름, 회사 타입, 공종명
   * 설계서 : [UAE_GSIL] Company Management_PC_화면설계서(v1.0)_24.10.08
   * 페이지 : 10P
   * 화면ID : CM_W_01
   *
   * @param {ReqCompanyList} params
   * @returns {ResCompanyList}
   */
  async getCompany(params: ReqCompanyList): Promise<ResCompanyList> {
    return await companyInstance
      .get<ResCompanyList>(`/company`, { params })
      .then((res) => res.data);
  },

  /**
   * 업체 상세 조회 API
   * companyId 를 통해 업체 상세 정보를 조회합니다.
   *
   * 설계서 : [UAE_GSIL] Company Management_PC_화면설계서(v1.0)_24.10.08
   * 페이지 : 13P
   * 화면ID : CM_WP_01
   *
   * @param {ReqCompanyDetail} data
   * @returns {CompanyInfo}
   */
  async getCompanyDetail(data: ReqCompanyDetail): Promise<ResCompanyDetail> {
    return await companyInstance
      .get<ResCompanyDetail>(`/company/${data.companyId}`)
      .then((res) => res.data);
  },

  async getCompanyName(data: ReqCompanyDetail): Promise<ResCompanyDetail> {
    return await companyInstance
        .get<ResCompanyDetail>(`/company/name/${data.companyId}`)
        .then((res) => res.data);
  },

  /**
   * company 이름 목록 조회
   * @returns
   */
  async getCompanyNames(params: any) {
    return await companyInstance.get(`/company/names`, { params }).then((res) => res.data);
  },

  /**
   * company 수정
   */
  async updateCompany(data: any) {
    return await companyInstance
      .post(`/company/modify`, data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((res) => res.data);
  },

  /**
   * company 삭제
   * @param {companyId} id
   * @returns {id}
   */
  async deleteCompany(id: string) {
    return await companyInstance.delete(`/company/${id}`).then((res) => res.data);
  },

  /**
   * company 사진 조회
   * @param {fileId} id
   * @returns
   */
  async getCompanyPicAPI(id: string) {
    return await companyInstance
      .get(`/company/downloads/${id}`, {
        responseType: 'blob'
      })
      .then((res) => {
        let imageURL = URL.createObjectURL(res.data);
        return imageURL;
      });
  }
};

export class CompanyAPI {
  Axios: AxiosInstance;

  constructor() {
    this.Axios = new AxiosInstanceCreator().create();
  }

  /** Company 리스트 조회 */
  async getCompanies(params: ReqCompanyList): Promise<BaseApiResponse<unknown, CompanyInfo>> {
    return companyInstance.get(`/company`, { params }).then((res) => res.data);
  }

  /** Company 이름 조회 */
  async getCompanyNames(
    params: BaseSearchRequest
  ): Promise<BaseApiResponse<unknown, ResCompanyNames>> {
    return await this.Axios.get<BaseApiResponse<unknown, ResCompanyNames>>(`/company/names`, {
      params
    }).then((res) => res.data);
  }

  /**
   * Company 추가
   */
  async createCompany(data: FormData): Promise<BaseApiResponse> {
    return companyInstance
      .post(`/company`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => res.data);
  }

  /**
   * company 수정
   */
  async updateCompany(data: ReqUpdateCompany): Promise<BaseApiResponse> {
    return companyInstance
      .put(`/company/${data.comapnyId}`, data.updateCompany)
      .then((res) => res.data);
  }

  /**
   * company 파일 수정
   */
  async updateCompanyFiles(companyId: string, data: FormData): Promise<BaseApiResponse> {
    return companyInstance
      .put(`/company/${companyId}/file`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  }

  /**
   * company 파일 삭제
   */
  async deleteCompanyFiles(companyId: string): Promise<BaseApiResponse> {
    return companyInstance.delete(`/company/${companyId}/files`).then((res) => res.data);
  }

  /**
   * Compnay 상세 조회
   */
  async getCompanyDetail(data: ReqCompanyDetail): Promise<BaseApiResponse<ICompanyDetail>> {
    return companyInstance.get(`/company/${data.companyId}`).then((res) => res.data);
  }
}
